import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { useMemo } from 'react';
import { getPricingCategories } from 'src/api/getPricingCategories/getPricingCategories';
import { INestedPricingCategory, IPricingCategory } from 'src/api/getPricingCategories/types';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { buildNestedCategories } from 'src/utils/venueCategories';
import { flattenHierarchy } from 'src/helpers/flattenHierarchy';
import { QueryKeys } from '../QueryKeys';

type IPricingCategoriesResponse = UseQueryResult<IPricingCategory[]> & {
  nestedCategories: INestedPricingCategory[];
  orderedFlatCategories: IPricingCategory[];
  queryKey: string[];
};

export const usePricingCategories = (
  priceSchemeId: string,
  options?: UseQueryOptions<IPricingCategory[]>,
): IPricingCategoriesResponse => {
  const { showSnackbar } = useSnackbar();

  const queryKey = [getBrandId(), QueryKeys.pricingCategories, priceSchemeId];
  const query = useQuery({
    queryKey,
    queryFn: () => getPricingCategories(priceSchemeId),
    onError: () =>
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong while fetching pricing categories',
      }),
    ...options,
  });

  const nestedCategories = useMemo(() => {
    if (!query.data) return [];
    return buildNestedCategories<IPricingCategory>(query.data);
  }, [query.data]);

  const orderedFlatCategories = useMemo(() => {
    return flattenHierarchy(nestedCategories);
  }, [nestedCategories]);

  return {
    ...query,
    queryKey,
    nestedCategories,
    orderedFlatCategories,
  };
};
